import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  BookOpen,
  Loader2,
  AlertCircle,
  Trash2,
  Edit3,
  ChevronRight,
} from 'lucide-react'
import { adminApi, curriculumBuilderApi, type AdminContent } from '@/api/admin'
import { CreateContentModal } from './CreateContentModal'

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  ready: 'bg-yellow-100 text-yellow-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-red-100 text-red-600',
}

const STATUS_LABELS: Record<string, string> = {
  draft: '초안',
  ready: '준비됨',
  published: '발행됨',
  archived: '보관됨',
}

export function CurriculumList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data: contents, isLoading, error } = useQuery({
    queryKey: ['admin', 'contents', { hierarchyLevel: 'curriculum', origin: 'direct' }],
    queryFn: () => adminApi.getContents({ hierarchyLevel: 'curriculum', size: 100 }),
    select: (data) => data.content.filter((c: AdminContent) => c.origin === 'direct'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => curriculumBuilderApi.deleteContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'contents'] })
    },
  })

  const handleCreate = () => setShowCreateModal(true)

  const handleCreated = (content: AdminContent) => {
    setShowCreateModal(false)
    queryClient.invalidateQueries({ queryKey: ['admin', 'contents'] })
    navigate(`/contents/curriculum-builder/${content.id}`)
  }

  const handleDelete = (id: number, title: string) => {
    if (confirm(`"${title}" 커리큘럼을 삭제하시겠습니까?\n하위 코스/모듈도 함께 삭제됩니다.`)) {
      deleteMutation.mutate(id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-lg">
        <AlertCircle className="w-5 h-5" />
        <span>커리큘럼 목록을 불러올 수 없습니다.</span>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">커리큘럼 빌더</h1>
          <p className="text-sm text-slate-500 mt-1">기존 레슨을 조합하여 새로운 커리큘럼을 구성합니다</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          새 커리큘럼
        </button>
      </div>

      {contents && contents.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
          <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">아직 직접 생성한 커리큘럼이 없습니다</h3>
          <p className="text-sm text-slate-400 mb-6">새 커리큘럼을 만들어 기존 레슨을 조합해보세요</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            첫 커리큘럼 만들기
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {contents?.map((curriculum) => (
            <div
              key={curriculum.id}
              className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all"
            >
              <div
                className="flex items-center gap-4 flex-1 cursor-pointer"
                onClick={() => navigate(`/contents/curriculum-builder/${curriculum.id}`)}
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900">{curriculum.title}</h3>
                  {curriculum.shortDescription && (
                    <p className="text-sm text-slate-500 mt-0.5">{curriculum.shortDescription}</p>
                  )}
                </div>
                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[curriculum.status] || STATUS_COLORS.draft}`}>
                  {STATUS_LABELS[curriculum.status] || curriculum.status}
                </span>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => navigate(`/contents/curriculum-builder/${curriculum.id}`)}
                  className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="편집"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(curriculum.id, curriculum.title)}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="삭제"
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateContentModal
          level="curriculum"
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}
